import { MenuItem, Select } from "@mui/material";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components";
import { SkillAccessibility } from "@/shared/domain/skills";

import {
  ACCESSIBILITY_MENU_COPY,
  ACCESSIBILITY_ORDER,
} from "./skillAccessibility";
import { VisibilityChip } from "./VisibilityChip";
import styles from "./VisibilitySelect.module.css";

interface Props {
  accessibility: SkillAccessibility;
  /** True while a change is in flight — collapses to a spinner-bearing chip. */
  isPending: boolean;
  onChange: (next: SkillAccessibility) => void;
}

/**
 * Author-facing dropdown for a skill's accessibility tier, collapsed to the
 * same {@link VisibilityChip} a non-author sees so the two states read alike.
 *
 * Shared by the Skills table's visibility column and the skill page's header —
 * changing who can see a skill is one control, wherever you meet it.
 *
 * The tooltip is controlled rather than left in MUI's default hover mode so it
 * can be force-closed while the menu is open: an uncontrolled tooltip stays
 * mounted on top of the open dropdown list and obscures the tier options.
 */
export function VisibilitySelect({
  accessibility,
  isPending,
  onChange,
}: Props) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [hovering, setHovering] = React.useState(false);
  // Closing the menu unmounts its overlay, which puts the pointer back over the
  // select and fires one synthetic mouseover. Without this flag that reopens
  // the tooltip the instant a tier is picked, and it sits there over the page.
  // Set on menu close and consumed by that one re-entry, so the tooltip is back
  // to normal on the next genuine hover.
  const [suppressed, setSuppressed] = React.useState(false);
  return (
    <RobotoTooltip
      title="Change who can see and edit this skill"
      open={hovering && !menuOpen && !suppressed}
      onOpen={() => {
        if (suppressed) {
          // The menu-close re-entry. Swallow it — don't open — and clear the
          // flag so the *next* hover behaves normally.
          setSuppressed(false);
          return;
        }
        setHovering(true);
      }}
      onClose={() => {
        setHovering(false);
        setSuppressed(false);
      }}
    >
      <span className={styles.wrapper}>
        <Select
          size="small"
          variant="standard"
          disableUnderline
          value={accessibility}
          disabled={isPending}
          className={styles.select}
          open={menuOpen}
          onOpen={() => setMenuOpen(true)}
          onClose={() => {
            setMenuOpen(false);
            setSuppressed(true);
          }}
          onChange={(e) => onChange(e.target.value as SkillAccessibility)}
          inputProps={{ "aria-label": "Accessibility" }}
          renderValue={(value) => (
            <VisibilityChip accessibility={value} pending={isPending} />
          )}
        >
          {ACCESSIBILITY_ORDER.map((tier) => (
            <MenuItem key={tier} value={tier}>
              {ACCESSIBILITY_MENU_COPY[tier]}
            </MenuItem>
          ))}
        </Select>
      </span>
    </RobotoTooltip>
  );
}
